import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import StartProjectCTA from "@/components/feature/StartProjectCTA";
import HeroReveal from "@/components/feature/HeroReveal";
import { services } from "@/mocks/services";

/* ─── Assets ─────────────────────────────────────────────── */
const HERO_IMG     = "https://public.readdy.ai/ai/img_res/sandbox/7ffb55af637baf94343ad3a63d8fcd76/f86dfac6-e65e-48c9-9f8a-ad5fd5c49543_home-hero-ridge-house.webp";
const HERO_BEFORE_IMG = "https://public.readdy.ai/ai/img_res/sandbox/7ffb55af637baf94343ad3a63d8fcd76/1c295ab9-0183-488d-a7b0-fb6f5e4368eb_hero-before-aged-interior.webp";
const A04_IMG      = "https://public.readdy.ai/ai/img_res/sandbox/7ffb55af637baf94343ad3a63d8fcd76/f86dfac6-e65e-48c9-9f8a-ad5fd5c49543_project-ridge-house-hero.webp";
const MATERIAL_IMG = "https://public.readdy.ai/ai/img_res/sandbox/7ffb55af637baf94343ad3a63d8fcd76/f86dfac6-e65e-48c9-9f8a-ad5fd5c49543_project-ridge-house-detail.webp";
const A05_IMG      = "https://public.readdy.ai/ai/img_res/sandbox/7ffb55af637baf94343ad3a63d8fcd76/f86dfac6-e65e-48c9-9f8a-ad5fd5c49543_project-oakline-kitchen-hero.webp";
const A06_IMG      = "https://public.readdy.ai/ai/img_res/sandbox/7ffb55af637baf94343ad3a63d8fcd76/f86dfac6-e65e-48c9-9f8a-ad5fd5c49543_project-stonewell-bath-hero.webp";
const PROCESS_SUPPORT_IMG = "https://public.readdy.ai/ai/img_res/sandbox/7ffb55af637baf94343ad3a63d8fcd76/f86dfac6-e65e-48c9-9f8a-ad5fd5c49543_process-design-review.webp";
const SERVICES_IMG = "https://public.readdy.ai/ai/img_res/sandbox/7ffb55af637baf94343ad3a63d8fcd76/f86dfac6-e65e-48c9-9f8a-ad5fd5c49543_home-services-support.webp";
const STUDIO_IMG = "https://public.readdy.ai/ai/img_res/sandbox/7ffb55af637baf94343ad3a63d8fcd76/f86dfac6-e65e-48c9-9f8a-ad5fd5c49543_studio-principal-at-work.webp";

/* ─── Data ───────────────────────────────────────────────── */
type FeaturedProject = {
  id: string; slug: string; num: string; title: string;
  type: string; location: string; image: string; description: string;
};

const featuredProjects: FeaturedProject[] = [
  {
    id: "ridge", slug: "ridge-house", num: "001", title: "Ridge House",
    type: "Whole Home Renovation", location: "Los Angeles, California", image: A04_IMG,
    description: "A hillside residence reimagined around light, view and quiet materiality — white oak, travertine and honed limestone composed into a calm daily rhythm.",
  },
  {
    id: "oakline", slug: "oakline-kitchen", num: "002", title: "Oakline Kitchen",
    type: "Kitchen Renovation", location: "Pasadena, California", image: A05_IMG,
    description: "A working family kitchen anchored by a single travertine island — quiet oak cabinetry, honed stone and brass fittings tuned to daily use.",
  },
  {
    id: "stonewell", slug: "stonewell-bath", num: "003", title: "Stonewell Bath",
    type: "Primary Bath Renovation", location: "Ojai, California", image: A06_IMG,
    description: "A primary bath resolved as one continuous stone volume — freestanding tub, warm oak vanity and diffused daylight shaping a slow, restorative room.",
  },
];

/* ─── Math ───────────────────────────────────────────────── */
function clamp01(v: number) { return Math.min(1, Math.max(0, v)); }
function easeInOut(t: number) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
function easeOut(t: number)   { return 1 - Math.pow(1 - t, 3); }
function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }
function mapRange(v: number, a: number, b: number) { return clamp01((v - a) / (b - a)); }

/* ═══════════════════════════════════════════════════════════
   Reusable sticky project section (UNCHANGED)
   ═══════════════════════════════════════════════════════════ */
function StickyProjectSection({ project }: { project: FeaturedProject }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const textRef  = useRef<HTMLDivElement>(null);
  const topBlockRef = useRef<HTMLDivElement>(null);
  const bottomBlockRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const track = trackRef.current;
    const frame = frameRef.current;
    const text  = textRef.current;
    const topBlock = topBlockRef.current;
    const bottomBlock = bottomBlockRef.current;
    if (!track || !frame || !text || !topBlock || !bottomBlock) return;

    let vw = window.innerWidth;
    let vh = window.innerHeight;
    let trackTop = 0;
    let trackHeight = 0;

    let sidePad = 128, editW = 1120, editH = 840;
    let scaleFit = 1, finalW = 0, finalH = 0, finalL = 0, finalT = 0;
    let topH = 0, bottomH = 0, bottomSide = 0;

    const recomputeGeometry = () => {
      vw = window.innerWidth;
      vh = window.innerHeight;
      sidePad = vw >= 1280 ? 128 : vw >= 1024 ? 96 : 48;
      editW = Math.min(1120, vw - sidePad);
      editH = (editW * 3) / 4;
      scaleFit = Math.min(1, (vh - 160) / editH);
      finalW = editW * scaleFit;
      finalH = editH * scaleFit;
      finalL = (vw - finalW) / 2;
      finalT = (vh - finalH) / 2;
      topH    = Math.max(48, finalT);
      bottomH = Math.max(48, vh - finalT - finalH);
      bottomSide = finalL;

      topBlock.style.top = "0px";
      topBlock.style.height = `${topH}px`;
      topBlock.style.left = `${bottomSide}px`;
      topBlock.style.right = `${bottomSide}px`;
      bottomBlock.style.bottom = "0px";
      bottomBlock.style.height = `${bottomH}px`;
      bottomBlock.style.left = `${bottomSide}px`;
      bottomBlock.style.right = `${bottomSide}px`;
    };

    const measureTrack = () => {
      const rect = track.getBoundingClientRect();
      trackTop = rect.top + window.scrollY;
      trackHeight = rect.height;
    };

    recomputeGeometry();
    measureTrack();

    let raf = 0;
    let running = true;
    let targetP = 0;
    let visP = 0;
    let lastT = performance.now();
    let lastAppliedFrame = { w: -1, h: -1, l: -1, t: -1 };
    let lastTextOp = -1;
    let lastTextY = -999;

    const tick = (now: number) => {
      if (!running) return;
      const dt = Math.min(64, now - lastT);
      lastT = now;

      const scrolled = window.scrollY - trackTop;
      const scrollable = trackHeight - vh;
      targetP = scrollable > 0 ? clamp01(scrolled / scrollable) : 0;

      const tau = 130;
      const alpha = 1 - Math.exp(-dt / tau);
      visP += (targetP - visP) * alpha;
      if (Math.abs(targetP - visP) < 0.0005) visP = targetP;

      const shrinkT = easeInOut(mapRange(visP, 0.12, 0.70));
      const textT   = easeOut(mapRange(visP, 0.52, 0.76));

      const cW = vw - shrinkT * (vw - finalW);
      const cH = vh - shrinkT * (vh - finalH);
      const cL = shrinkT * finalL;
      const cT = shrinkT * finalT;

      if (cW !== lastAppliedFrame.w || cH !== lastAppliedFrame.h ||
          cL !== lastAppliedFrame.l || cT !== lastAppliedFrame.t) {
        frame.style.width  = `${cW}px`;
        frame.style.height = `${cH}px`;
        frame.style.transform = `translate3d(${cL}px, ${cT}px, 0)`;
        lastAppliedFrame = { w: cW, h: cH, l: cL, t: cT };
      }

      const textOp = textT;
      const textY = (1 - textT) * 8;
      if (textOp !== lastTextOp || textY !== lastTextY) {
        text.style.opacity = `${textOp}`;
        text.style.transform = `translate3d(0, ${textY}px, 0)`;
        const visible = textOp > 0.01;
        text.style.visibility = visible ? "visible" : "hidden";
        text.style.pointerEvents = visible ? "auto" : "none";
        lastTextOp = textOp;
        lastTextY = textY;
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    const onResize = () => { recomputeGeometry(); measureTrack(); };
    window.addEventListener("resize", onResize);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <section ref={trackRef} className="relative w-full" style={{ height: "180vh" }}>
      <div ref={stageRef} className="sticky top-0 w-full overflow-hidden" style={{ height: "100svh", backgroundColor: "#F8F8F8" }}>
        <div ref={frameRef}
             className="absolute overflow-hidden top-0 left-0"
             style={{ willChange: "transform, width, height" }}>
          <img src={project.image} alt={`${project.title} — ${project.type}`}
               className="w-full h-full block"
               style={{ objectFit: "cover", objectPosition: "center" }} />
        </div>

        <div ref={textRef}
             className="absolute inset-0 pointer-events-none"
             style={{ opacity: 0, visibility: "hidden", willChange: "opacity, transform", zIndex: 5 }}>
          <div ref={topBlockRef} className="absolute">
            <div className="w-full h-full flex items-end pb-5">
              <div className="w-full flex items-end justify-between gap-8">
                <div className="flex flex-col gap-3">
                  <span className="eyebrow">Selected Work — {project.num}</span>
                  <h2 className="font-serif-display font-light leading-[1.02]"
                      style={{ fontSize: "clamp(30px,3.2vw,44px)", letterSpacing: "-0.02em", color: "#1D1A17" }}>
                    {project.title}
                  </h2>
                </div>
                <div className="text-right pb-1">
                  <div className="text-ink-soft leading-[1.7]" style={{ fontSize: "13px", letterSpacing: "0.01em" }}>
                    {project.type}<br />{project.location}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div ref={bottomBlockRef} className="absolute">
            <div className="w-full h-full flex items-start pt-5">
              <div className="w-full flex items-start justify-between gap-12">
                <p className="text-ink-soft leading-[1.65] max-w-[560px]"
                   style={{ fontSize: "clamp(14px,1vw,16px)" }}>
                  {project.description}
                </p>
                <div className="shrink-0 pt-1">
                  <Link to={`/projects/${project.slug}`}
                     className="arrow-link text-[12px] tracking-[0.1em] uppercase text-ink hover:text-accent transition-colors cursor-pointer whitespace-nowrap">
                    View Project
                    <i className="ri-arrow-right-line arrow w-4 h-4 flex items-center justify-center" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Mobile card ────────────────────────────────────────── */
function MobileProjectCard({ project }: { project: FeaturedProject }) {
  return (
    <div className="mx-auto max-w-content px-5 py-20">
      <div className="flex flex-col gap-3 mb-8">
        <span className="eyebrow">Selected Work — {project.num}</span>
        <h2 className="font-serif-display font-light leading-[1.04]"
            style={{ fontSize: "34px", letterSpacing: "-0.02em", color: "#1D1A17" }}>
          {project.title}
        </h2>
        <div className="text-ink-soft leading-[1.7]" style={{ fontSize: "13px" }}>
          {project.type} · {project.location}
        </div>
      </div>
      <div className="w-full overflow-hidden">
        <img src={project.image} alt={`${project.title} — ${project.type}`}
             className="w-full block" style={{ aspectRatio: "4/3", objectFit: "cover" }} />
      </div>
      <div className="mt-8 flex flex-col gap-6">
        <p className="text-ink-soft leading-[1.65]" style={{ fontSize: "15px" }}>{project.description}</p>
        <Link to={`/projects/${project.slug}`} className="arrow-link text-[12px] tracking-[0.1em] uppercase text-ink cursor-pointer">
          View Project
          <i className="ri-arrow-right-line arrow w-4 h-4 flex items-center justify-center" />
        </Link>
      </div>
    </div>
  );
}

/* ─── Bleed Section Layout ───────────────────────────────── */
/*
  img-side: "left" | "right"
  The image bleeds to the viewport edge on its side.
  The text panel stays in the normal container on the other side.
  Fixed height: 100vh
*/
const SECTION_H = "100svh";

function BleedSection({
  id, eyebrow, imgSrc, imgAlt, imgSide,
  children, bg = "#F8F8F8",
}: {
  id?: string; eyebrow: string; imgSrc: string; imgAlt: string;
  imgSide: "left" | "right"; children: ReactNode; bg?: string;
}) {
  const isLeft = imgSide === "left";
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

  return (
    <section
      ref={sectionRef}
      id={id}
      className="relative w-full overflow-hidden hidden md:block"
      style={{ height: SECTION_H, backgroundColor: bg }}
    >
      {/* Image — bleeds to its viewport edge, slides in from outside */}
      <div
        className="absolute top-0 bottom-0 flex-none"
        style={{
          [isLeft ? "left" : "right"]: 0,
          width: "50vw",
          transform: inView
            ? "translate3d(0, 0, 0)"
            : `translate3d(${isLeft ? "-100%" : "100%"}, 0, 0)`,
          transition: `transform 1000ms ${EASE}`,
          willChange: "transform",
        }}
      >
        <img
          src={imgSrc}
          alt={imgAlt}
          className="w-full h-full block"
          style={{ objectFit: "cover", objectPosition: "center" }}
        />
      </div>

      {/* Text — stays in container, on the opposite side */}
      <div
        className="relative z-10 flex items-center w-full h-full"
        style={{
          justifyContent: isLeft ? "flex-end" : "flex-start",
        }}
      >
        <div
          className="flex flex-col justify-between h-full py-16"
          style={{
            width: "50vw",
            [isLeft ? "paddingLeft" : "paddingRight"]: "clamp(32px, 4vw, 80px)",
            [isLeft ? "paddingRight" : "paddingLeft"]: "clamp(32px, 5vw, 96px)",
            transform: inView ? "translate3d(0, 0, 0)" : "translate3d(0, 20px, 0)",
            opacity: inView ? 1 : 0,
            transition: `transform 800ms ${EASE} 200ms, opacity 800ms ${EASE} 200ms`,
            willChange: "transform, opacity",
          }}
        >
          <span className="eyebrow">{eyebrow}</span>
          <div>{children}</div>
        </div>
      </div>
    </section>
  );
}

/* Mobile fallback for bleed sections */
function BleedSectionMobile({
  eyebrow, imgSrc, imgAlt, children, bg = "#F8F8F8",
}: {
  eyebrow: string; imgSrc: string; imgAlt: string;
  children: ReactNode; bg?: string;
}) {
  return (
    <section className="w-full flex flex-col md:hidden py-16 px-5" style={{ backgroundColor: bg }}>
      <span className="eyebrow mb-8">{eyebrow}</span>
      <div className="w-full overflow-hidden mb-8" style={{ aspectRatio: "4/3" }}>
        <img src={imgSrc} alt={imgAlt} className="w-full h-full block" style={{ objectFit: "cover" }} />
      </div>
      {children}
    </section>
  );
}

/* ─── 03 Materials ───────────────────────────────────────── */
function MaterialsSection() {
  const content = (
    <>
      <h3 className="font-serif-display leading-[1.06]"
          style={{ fontSize: "clamp(28px,2.4vw,38px)", letterSpacing: "-0.018em", color: "#1D1A17" }}>
        Craft is where design becomes tangible.
      </h3>
      <p className="mt-6 text-ink-soft leading-[1.7]" style={{ fontSize: "15px" }}>
        Custom joinery, stone transitions and hardware are drawn, sampled and resolved
        alongside the plan — never left to the site.
      </p>
      <Link to="/studio" className="mt-7 inline-flex arrow-link text-[11px] tracking-[0.12em] uppercase text-ink cursor-pointer">
        Studio Details
        <i className="ri-arrow-right-line arrow w-4 h-4 flex items-center justify-center" />
      </Link>
    </>
  );
  return (
    <>
      <BleedSection eyebrow="03 — Materials &amp; Making" imgSrc={MATERIAL_IMG}
        imgAlt="Detail of travertine island edge meeting white oak joinery"
        imgSide="left" bg="#F8F8F8">
        {content}
      </BleedSection>
      <BleedSectionMobile eyebrow="03 — Materials &amp; Making" imgSrc={MATERIAL_IMG}
        imgAlt="Detail of travertine island edge meeting white oak joinery" bg="#F8F8F8">
        {content}
      </BleedSectionMobile>
    </>
  );
}

/* ─── 04 Services ────────────────────────────────────────── */
function ServicesSection() {
  const list = (
    <>
      <ul>
        <li className="border-t hairline" />
        {services.map(s => (
          <li key={s.slug} className="border-b hairline">
            <Link to={`/services/${s.slug}`}
                  className="group grid grid-cols-12 items-center gap-4 py-5 cursor-pointer">
              <span className="col-span-2 eyebrow text-ink-soft">{s.num}</span>
              <span className="col-span-8 font-serif-display font-light group-hover:text-accent transition-colors"
                    style={{ fontSize: "clamp(18px,1.7vw,26px)", letterSpacing: "-0.014em" }}>
                {s.title}
              </span>
              <span className="col-span-2 flex justify-end">
                <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center text-ink-soft group-hover:text-accent transition-all group-hover:translate-x-1"
                   style={{ fontSize: "18px" }} />
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <div className="mt-8">
        <Link to="/services" className="arrow-link text-[11px] tracking-[0.14em] uppercase text-ink cursor-pointer">
          View All Services
          <i className="ri-arrow-right-line arrow w-4 h-4 flex items-center justify-center" />
        </Link>
      </div>
    </>
  );

  const heading = (
    <>
      <h2 className="font-serif-display leading-[1.08] mb-8"
          style={{ fontSize: "clamp(26px,2.2vw,36px)", letterSpacing: "-0.018em" }}>
        One integrated team from early design through construction.
      </h2>
      {list}
    </>
  );

  return (
    <>
      <BleedSection eyebrow="04 — What We Do" imgSrc={SERVICES_IMG}
        imgAlt="Detail of white oak millwork and honed limestone counter"
        imgSide="right" bg="#FFFFFF">
        {heading}
      </BleedSection>
      <BleedSectionMobile eyebrow="04 — What We Do" imgSrc={SERVICES_IMG}
        imgAlt="Detail of white oak millwork and honed limestone counter" bg="#FFFFFF">
        {heading}
      </BleedSectionMobile>
    </>
  );
}

/* ─── 05 Process ─────────────────────────────────────────── */
function ProcessSection() {
  const steps = [
    { n: "01", t: "Discover", b: "Site visit, brief and feasibility." },
    { n: "02", t: "Design",   b: "Concept, plan and material direction." },
    { n: "03", t: "Plan",     b: "Documentation, permitting and pricing." },
    { n: "04", t: "Build",    b: "Construction managed by the same team." },
    { n: "05", t: "Complete", b: "Handover, punch list and aftercare." },
  ];

  const content = (
    <>
      <h2 className="font-serif-display leading-[1.08] mb-8"
          style={{ fontSize: "clamp(24px,2vw,34px)", letterSpacing: "-0.018em" }}>
        From the first conversation to the final walkthrough, one team stays accountable throughout.
      </h2>
      <ul>
        <li className="border-t hairline" />
        {steps.map(s => (
          <li key={s.n} className="border-b hairline">
            <div className="grid grid-cols-12 gap-3 py-4 items-baseline">
              <span className="col-span-2 eyebrow text-ink-soft">{s.n}</span>
              <span className="col-span-4 font-serif-display font-light"
                    style={{ fontSize: "clamp(16px,1.4vw,22px)", letterSpacing: "-0.014em" }}>
                {s.t}
              </span>
              <span className="col-span-6 text-ink-soft leading-[1.55]"
                    style={{ fontSize: "13px" }}>
                {s.b}
              </span>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-8">
        <Link to="/process" className="arrow-link text-[11px] tracking-[0.14em] uppercase text-ink cursor-pointer">
          Full Process
          <i className="ri-arrow-right-line arrow w-4 h-4 flex items-center justify-center" />
        </Link>
      </div>
    </>
  );

  return (
    <>
      <BleedSection eyebrow="05 — Our Process" imgSrc={PROCESS_SUPPORT_IMG}
        imgAlt="Architect on site reviewing drawings during construction"
        imgSide="left" bg="#F8F8F8">
        {content}
      </BleedSection>
      <BleedSectionMobile eyebrow="05 — Our Process" imgSrc={PROCESS_SUPPORT_IMG}
        imgAlt="Architect on site reviewing drawings during construction" bg="#F8F8F8">
        {content}
      </BleedSectionMobile>
    </>
  );
}

/* ─── 06 Studio ──────────────────────────────────────────── */
function StudioSection() {
  const content = (
    <>
      <h3 className="font-serif-display leading-[1.06]"
          style={{ fontSize: "clamp(28px,2.4vw,38px)", letterSpacing: "-0.018em", color: "#1D1A17" }}>
        A single team for design, architecture, construction and management.
      </h3>
      <p className="mt-6 text-ink-soft leading-[1.7]" style={{ fontSize: "15px" }}>
        Atelier Ridge is a residential design-build studio composing calm, crafted homes
        across Southern California. Design leads the work — construction makes sure it lands.
      </p>
      <Link to="/studio" className="mt-7 inline-flex arrow-link text-[11px] tracking-[0.12em] uppercase text-ink cursor-pointer">
        About the Studio
        <i className="ri-arrow-right-line arrow w-4 h-4 flex items-center justify-center" />
      </Link>
    </>
  );

  return (
    <>
      <BleedSection eyebrow="06 — Studio" imgSrc={STUDIO_IMG}
        imgAlt="Atelier Ridge studio — architect reviewing drawings"
        imgSide="right" bg="#FFFFFF">
        {content}
      </BleedSection>
      <BleedSectionMobile eyebrow="06 — Studio" imgSrc={STUDIO_IMG}
        imgAlt="Atelier Ridge studio — architect reviewing drawings" bg="#FFFFFF">
        {content}
      </BleedSectionMobile>
    </>
  );
}

/* ─── 07 Client Perspective — Testimonial Carousel ───── */
type Testimonial = {
  quote: string;
  name: string;
  role: string;
  project: string;
};

const testimonials: Testimonial[] = [
  {
    quote: "From the first conversation to the final walkthrough, the process always felt clear, thoughtful and considered. They understood the way our family actually lives.",
    name: "Elena & Marcus Hollister",
    role: "Homeowners",
    project: "Ridge House — Los Angeles",
  },
  {
    quote: "Every material decision was drawn, sampled and resolved before anything reached the site. We never once felt like we were guessing.",
    name: "Priya Chen",
    role: "Homeowner",
    project: "Oakline Kitchen — Pasadena",
  },
  {
    quote: "A rare studio where design and construction genuinely speak the same language. The finished home is calmer than we imagined possible.",
    name: "David Whitaker",
    role: "Homeowner",
    project: "Stonewell Bath — Ojai",
  },
  {
    quote: "They treated our budget and timeline with the same rigor as the design itself. That balance is rare, and it made all the difference.",
    name: "Sofia Aranda",
    role: "Homeowner",
    project: "Canyon Guest House — Malibu",
  },
  {
    quote: "The details we never would have thought to ask about were the ones that quietly transformed the way the house feels every day.",
    name: "James & Rebecca Ito",
    role: "Homeowners",
    project: "Hillside Addition — Altadena",
  },
];

function ClientPerspectiveSection() {
  const loop = [...testimonials, ...testimonials];

  return (
    <section className="w-full py-24 md:py-28" style={{ backgroundColor: "#F8F8F8" }}>
      <div
        className="w-full"
        style={{ paddingLeft: "clamp(20px, 5vw, 96px)", paddingRight: "clamp(20px, 5vw, 96px)" }}
      >
        <div className="mb-12 md:mb-16">
          <span className="eyebrow">07 — Client Perspective</span>
        </div>

        <div
          className="relative w-full overflow-hidden marquee-mask"
          onMouseEnter={(e) => { e.currentTarget.querySelector<HTMLDivElement>(".marquee-track")!.style.animationPlayState = "paused"; }}
          onMouseLeave={(e) => { e.currentTarget.querySelector<HTMLDivElement>(".marquee-track")!.style.animationPlayState = "running"; }}
        >
          <div className="marquee-track flex gap-6 md:gap-8 w-max">
            {loop.map((t, idx) => (
              <article
                key={idx}
                className="flex-none w-[86vw] sm:w-[440px] md:w-[460px] border hairline p-8 md:p-10 flex flex-col"
                style={{ backgroundColor: "#FFFFFF", minHeight: "300px" }}
              >
                <blockquote
                  className="font-serif-display italic leading-[1.4] text-ink"
                  style={{ fontSize: "clamp(16px,1.15vw,19px)", letterSpacing: "-0.008em" }}
                >
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <div className="mt-auto pt-5 border-t hairline">
                  <div className="font-serif-display text-ink" style={{ fontSize: "16px" }}>
                    {t.name}
                  </div>
                  <div className="mt-1 text-ink-soft" style={{ fontSize: "12.5px" }}>
                    {t.role} · {t.project}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   HOME
   ═══════════════════════════════════════════════════════════ */
export default function Home() {
  const [heroIn,   setHeroIn]   = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const [navHidden, setNavHidden] = useState(false);
  const materialsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const upd = () => setIsDesktop(window.innerWidth >= 1024);
    upd();
    window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setHeroIn(true), 80);
    return () => clearTimeout(t);
  }, []);

  // Nav visibility:
  //  - visible on Hero (screen 01)
  //  - hidden the moment we start scrolling into 02 Selected Projects
  //  - visible again once 03 Materials fills the screen
  useEffect(() => {
    const check = () => {
      const el = materialsRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const y = window.scrollY;
      const inHero = y < vh * 0.6;           // still on the hero
      const inMaterialsOrBeyond = rect.top <= 0; // 03 has reached top
      setNavHidden(!(inHero || inMaterialsOrBeyond));
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, []);

  useEffect(() => {
    featuredProjects.forEach(p => {
      const img = new Image();
      img.src = p.image;
      if (typeof img.decode === "function") img.decode().catch(() => {});
    });
  }, []);

  void lerp;

  return (
    <div className="min-h-screen bg-warm-bg text-ink">
      <SiteNav transparentOnTop hidden={navHidden} />

      {/* ─── HERO — full-bleed image, editorial oversized heading ─── */}
      <HeroReveal
        afterSrc={HERO_IMG}
        beforeSrc={HERO_BEFORE_IMG}
        afterAlt="Ridge House interior — warm modern living space with travertine island"
        beforeAlt="Same home before renovation — aged interior with dated finishes"
        afterClassName={`hero-img ${heroIn ? "in" : ""}`}
        style={{ height: "100svh" }}
      >
        {/* Soft dark overlay for text readability */}
        {/* Bottom-only gradient — keeps the background bright and clear on top */}
        <div
          className="absolute inset-x-0 bottom-0 pointer-events-none"
          style={{
            height: "55%",
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.55) 80%, rgba(0,0,0,0.7) 100%)",
          }}
        />

        {/* Bottom-anchored content */}
        <div className="absolute inset-0 flex flex-col justify-end pointer-events-none">
          <div className="mx-auto max-w-page w-full px-5 md:px-12 lg:px-16 pb-6 md:pb-8">
            {/* Oversized editorial heading */}
            <h1
              className="font-serif-display font-light"
              style={{
                color: "#FFFFFF",
                fontSize: "clamp(42px, 6.6vw, 108px)",
                lineHeight: 1.02,
                letterSpacing: "-0.022em",
              }}
            >
              <span
                className="block text-left"
                style={{
                  transform: heroIn ? "translate3d(0,0,0)" : "translate3d(-8%,0,0)",
                  opacity: heroIn ? 1 : 0,
                  transition: "transform 1100ms cubic-bezier(0.22, 1, 0.36, 1) 120ms, opacity 900ms ease-out 120ms",
                  willChange: "transform, opacity",
                }}
              >
                Thoughtful spaces that
              </span>
              <span
                className="block text-right"
                style={{
                  transform: heroIn ? "translate3d(0,0,0)" : "translate3d(8%,0,0)",
                  opacity: heroIn ? 1 : 0,
                  transition: "transform 1100ms cubic-bezier(0.22, 1, 0.36, 1) 320ms, opacity 900ms ease-out 320ms",
                  willChange: "transform, opacity",
                }}
              >
                elevate everyday living
              </span>
            </h1>

            {/* Bottom row: description left, CTAs right */}
            <div className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8 md:gap-12">
              <p
                className="max-w-[720px] leading-[1.65]"
                style={{
                  color: "rgba(255,255,255,0.92)",
                  fontSize: "14px",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                Design-build interiors that reflect how you actually live — combining contemporary architecture, premium materials and thoughtful detailing into homes that feel calm and crafted.
              </p>

              <div className="flex items-stretch gap-6 md:shrink-0" style={{ pointerEvents: "auto" }}>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-10 py-4 bg-white text-ink text-[12px] tracking-[0.18em] uppercase hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get Started
                </Link>
                <Link
                  to="/services"
                  className="arrow-link inline-flex items-center text-[12px] tracking-[0.18em] uppercase cursor-pointer whitespace-nowrap"
                  style={{ color: "#FFFFFF" }}
                >
                  View Services
                  <i className="ri-arrow-right-up-line arrow w-4 h-4 flex items-center justify-center" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </HeroReveal>

      {/* ─── 02 SELECTED PROJECTS (unchanged) ───────────────── */}
      {isDesktop ? (
        <div id="work">
          {featuredProjects.map(p => (
            <StickyProjectSection key={p.id} project={p} />
          ))}
        </div>
      ) : (
        <section id="work" className="w-full" style={{ backgroundColor: "#F8F8F8" }}>
          {featuredProjects.map(p => (
            <MobileProjectCard key={p.id} project={p} />
          ))}
        </section>
      )}

      {/* ─── 03 MATERIALS ──────────────────────────────────── */}
      <div ref={materialsRef}>
        <MaterialsSection />
      </div>

      {/* ─── 04 SERVICES ───────────────────────────────────── */}
      <ServicesSection />

      {/* ─── 05 PROCESS ────────────────────────────────────── */}
      <ProcessSection />

      {/* ─── 06 STUDIO ─────────────────────────────────────── */}
      <StudioSection />

      {/* ─── 08 CLIENT PERSPECTIVE ─────────────────────────── */}
      <ClientPerspectiveSection />

      {/* ─── 09 FINAL CTA (full-bleed) ─────────────────────── */}
      <StartProjectCTA />

      {/* ─── FOOTER ────────────────────────────────────────── */}
      <SiteFooter />
    </div>
  );
}